import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL;

const BuildLookContext = createContext(null);

// Slot types and their category mappings
export const LOOK_SLOTS = {
  outfit: { label: "Your Outfit", emptyLabel: "Add Your Outfit", route: "/for-her", category: "for-her" },
  jewellery: { label: "Jewellery", emptyLabel: "Add Jewellery", route: "/accessories/jewellery-for-her", category: "jewellery-for-her" },
  clutch: { label: "Clutches & Potlis", emptyLabel: "Add Clutches & Potlis", route: "/accessories/cat-1773097441627", category: "clutches-potlis" },
  accessory: { label: "Something Extra", emptyLabel: "Add Something Extra", route: "/accessories", category: "accessories" },
};

// Discount tiers based on item count
const BUNDLE_DISCOUNTS = {
  2: 10,
  3: 15,
  4: 20,
};

export function BuildLookProvider({ children }) {
  const navigate = useNavigate();
  
  // Build look session state
  const [isActive, setIsActive] = useState(false);
  const [slots, setSlots] = useState({
    outfit: null,
    jewellery: null,
    clutch: null,
    accessory: null,
  });
  const [eventDate, setEventDate] = useState(null);
  const [rentalPeriod, setRentalPeriod] = useState(null);
  const [deliveryMethod, setDeliveryMethodState] = useState("pickup");
  const [activeSlot, setActiveSlot] = useState(null); // Which slot we're currently selecting for
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [loadingAI, setLoadingAI] = useState(false);
  const [lookMode, setLookMode] = useState(null); // null | "edit_current" | "start_fresh" | "build_another"
  const [cartLookItemIds, setCartLookItemIds] = useState([]); // IDs of look items in cart (for edit/replace)
  
  // Load state from sessionStorage on mount
  useEffect(() => {
    const saved = sessionStorage.getItem("mahra_build_look");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSlots(parsed.slots || {});
        setEventDate(parsed.eventDate || null);
        setRentalPeriod(parsed.rentalPeriod || null);
        setDeliveryMethodState(parsed.deliveryMethod || "pickup");
        setIsActive(parsed.isActive || false);
      } catch (e) {
        console.error("Failed to restore build look state:", e);
      }
    }
  }, []);
  
  // Persist state to sessionStorage
  useEffect(() => {
    if (isActive || Object.values(slots).some(Boolean)) {
      sessionStorage.setItem("mahra_build_look", JSON.stringify({
        slots,
        eventDate,
        rentalPeriod,
        deliveryMethod,
        isActive,
      }));
    }
  }, [slots, eventDate, rentalPeriod, deliveryMethod, isActive]);

  // Calculate totals
  const items = Object.values(slots).filter(Boolean);
  const itemCount = items.length;
  const discountPercent = BUNDLE_DISCOUNTS[itemCount] || (itemCount >= 4 ? 20 : 0);
  
  const originalTotal = items.reduce((sum, item) => sum + (item.rental_price || 0), 0);
  const discountAmount = (originalTotal * discountPercent) / 100;
  const finalPrice = originalTotal - discountAmount;
  const totalBond = items.reduce((sum, item) => sum + (item.bond_amount || item.deposit || 0), 0);
  
  // Progress (25% per slot, max 4 slots)
  const progress = Math.min(100, itemCount * 25);
  
  // Progress messages
  const getProgressMessage = () => {
    if (itemCount === 0) return "Start building your perfect look";
    if (itemCount === 1) return "Add an item to elevate your look and get 10% off";
    if (itemCount === 2) return "Add one more item to unlock 15% off";
    if (itemCount === 3) return "You're almost there — add one more accessory to unlock 20% off";
    return "Perfectly styled look ready to rent";
  };

  // Start building a look
  const startBuildLook = useCallback(() => {
    setIsActive(true);
    setActiveSlot("outfit");
    navigate("/build-your-look");
  }, [navigate]);

  // Select a slot to fill
  const selectSlot = useCallback((slotKey) => {
    if (!LOOK_SLOTS[slotKey]) return;
    setActiveSlot(slotKey);
    setIsActive(true);
    navigate(LOOK_SLOTS[slotKey].route + "?buildLook=true&slot=" + slotKey);
  }, [navigate]);

  // Add item to the active slot
  const addToLook = useCallback((product, slotKey = null) => {
    const targetSlot = slotKey || activeSlot;
    if (!targetSlot) {
      toast.error("No slot selected");
      return false;
    }
    
    // Check if the exact same product+variation is already in the look.
    // Two products with the same id but DIFFERENT variations are allowed —
    // e.g. Avni Necklace in Jewellery + Avni Earrings as an extra.
    //
    // The variation id MUST be normalised before comparison. Different callers
    // historically wrote it under different field names — `rental_option_id`
    // (canonical, used by BuildYourLookPage + cart/booking schema),
    // `selected_option_id` (legacy, OutfitDetail page), and `option_id`
    // (defensive). We accept all three on both sides.
    const _normVariationId = (it) =>
      it?.rental_option_id ?? it?.selected_option_id ?? it?.option_id ?? null;
    const incomingVariation = _normVariationId(product);
    const dupKey = `${product?.id || ""}::${incomingVariation || "default"}`;
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[BuildLook.addToLook] incoming dupKey =", dupKey, {
        product_id: product?.id,
        rental_option_id: product?.rental_option_id,
        selected_option_id: product?.selected_option_id,
      });
    }
    const existingSlot = Object.entries(slots).find(([_, item]) => {
      if (!item || item.id !== product.id) return false;
      return (_normVariationId(item) || null) === (incomingVariation || null);
    });
    if (existingSlot) {
      toast.error(
        incomingVariation
          ? "This variation is already in your look. Pick a different one to add another option."
          : "This item is already in your look"
      );
      return false;
    }
    
    // For outfit slot, lock in the event date and rental period
    if (targetSlot === "outfit" && !eventDate) {
      // Will be set from PDP
    }
    
    setSlots(prev => {
      const newSlots = {
        ...prev,
        [targetSlot]: {
          ...product,
          slot: targetSlot,
        },
      };
      
      // Count items and track discount unlock
      const newItemCount = Object.values(newSlots).filter(Boolean).length;
      const newDiscount = BUNDLE_DISCOUNTS[newItemCount] || (newItemCount >= 4 ? 20 : 0);
      const prevItemCount = Object.values(prev).filter(Boolean).length;
      const prevDiscount = BUNDLE_DISCOUNTS[prevItemCount] || (prevItemCount >= 4 ? 20 : 0);
      
      // Track discount unlock if new tier reached
      if (newDiscount > prevDiscount) {
        trackEvent("discount_unlocked", "", { 
          item_count: newItemCount, 
          discount_percent: newDiscount 
        });
      }
      
      // Track item addition
      trackEvent(targetSlot === "outfit" ? "outfit_added" : "accessory_added", product.id, { 
        slot: targetSlot 
      });
      
      return newSlots;
    });
    
    setActiveSlot(null);
    toast.success(`${product.name} added to your look`);
    navigate("/build-your-look");
    return true;
  }, [activeSlot, slots, eventDate, navigate]);

  // Set event date (locks in for all items)
  const lockEventDate = useCallback((date) => {
    setEventDate(date);
  }, []);

  // Set rental period (locks in for all items)
  const lockRentalPeriod = useCallback((period) => {
    setRentalPeriod(period);
  }, []);

  const lockDeliveryMethod = useCallback((method) => {
    setDeliveryMethodState(method);
  }, []);

  // Remove item from slot
  const removeFromSlot = useCallback((slotKey) => {
    setSlots(prev => ({
      ...prev,
      [slotKey]: null,
    }));
    
    // If removing outfit, clear date/period locks
    if (slotKey === "outfit") {
      setEventDate(null);
      setRentalPeriod(null);
    }
    
    toast.success("Item removed from look");
  }, []);

  // Replace item in slot (navigate to category)
  const replaceSlot = useCallback((slotKey) => {
    setActiveSlot(slotKey);
    navigate(LOOK_SLOTS[slotKey].route + "?buildLook=true&slot=" + slotKey);
  }, [navigate]);

  // Reset entire look
  const resetLook = useCallback(() => {
    setSlots({
      outfit: null,
      jewellery: null,
      clutch: null,
      accessory: null,
    });
    setEventDate(null);
    setRentalPeriod(null);
    setActiveSlot(null);
    setAiRecommendations([]);
    sessionStorage.removeItem("mahra_build_look");
    toast.success("Look cleared");
  }, []);

  // Exit build mode
  const exitBuildMode = useCallback(() => {
    setIsActive(false);
    setActiveSlot(null);
  }, []);

  // Fetch AI recommendations based on outfit
  const fetchAIRecommendations = useCallback(async (category = null) => {
    const outfitId = slots.outfit?.id;
    if (!outfitId) {
      setAiRecommendations([]);
      return;
    }
    
    setLoadingAI(true);
    try {
      let url = `${API}/api/ai/recommendations/${outfitId}`;
      if (category) {
        url += `?category=${encodeURIComponent(category)}`;
      }
      const res = await axios.get(url);
      // Use build_the_look (accessories that complement the outfit) instead of
      // recommendations (which also includes style_inspiration with outfits)
      const btl = res.data.build_the_look || res.data.recommendations || [];
      // Filter out garment categories - only show accessories
      const garmentCats = ["lehengas", "sarees", "indo-western", "anarkalis", "shararas", "for-her"];
      const accessoriesOnly = btl.filter(p => {
        const cat = (p.category || "").toLowerCase();
        return !garmentCats.some(g => cat.includes(g));
      });
      setAiRecommendations(accessoriesOnly);
    } catch (err) {
      console.error("Failed to fetch AI recommendations:", err);
      setAiRecommendations([]);
    } finally {
      setLoadingAI(false);
    }
  }, [slots.outfit?.id]);

  // Load look from external data (saved look or cart look items)
  const loadLookFromData = useCallback((slotsData, date, period, method) => {
    setSlots(slotsData || { outfit: null, jewellery: null, clutch: null, accessory: null });
    setEventDate(date || null);
    setRentalPeriod(period || null);
    setDeliveryMethodState(method || "pickup");
    setIsActive(true);
    setActiveSlot(null);
  }, []);

  // Add all items to cart
  const rentEntireLook = useCallback(async () => {
    if (itemCount < 2) {
      toast.error("Please add at least 2 items to your look");
      return false;
    }
    
    if (!eventDate || !rentalPeriod) {
      toast.error("Please select event date and rental period");
      return false;
    }
    
    const sessionId = localStorage.getItem("mahra_session_id") || "";
    const headers = { "x-session-id": sessionId, "Content-Type": "application/json" };
    
    try {
      // If editing or replacing, remove old look items from cart first
      if ((lookMode === "edit_current" || lookMode === "start_fresh") && cartLookItemIds.length > 0) {
        for (const oldId of cartLookItemIds) {
          try {
            await axios.delete(`${API}/api/cart/${oldId}`, { headers: { "x-session-id": sessionId } });
          } catch (e) { /* item may already be removed */ }
        }
        setCartLookItemIds([]);
      }

      // Generate a unique look group ID for this look
      const lookGroupId = lookMode === "build_another"
        ? `mahra-styled-look-${Date.now()}`
        : "mahra-styled-look";

      // Add outfit first (main item)
      const outfit = slots.outfit;
      if (outfit) {
        await axios.post(`${API}/api/cart/add`, {
          outfit_id: outfit.id,
          event_date: eventDate,
          duration: rentalPeriod,
          delivery_method: deliveryMethod,
          rental_option_id: outfit.selected_option_id || outfit.rental_option_id || null,
          rental_option_name: outfit.rental_option_name || null,
          is_look_item: true,
          look_group: lookGroupId,
        }, { headers });
      }
      
      // Add accessories
      for (const slotKey of ["jewellery", "clutch", "accessory"]) {
        const item = slots[slotKey];
        if (item) {
          await axios.post(`${API}/api/cart/add`, {
            outfit_id: item.id,
            event_date: eventDate,
            duration: rentalPeriod,
            delivery_method: deliveryMethod,
            rental_option_id: item.selected_option_id || item.rental_option_id || null,
            rental_option_name: item.rental_option_name || null,
            is_look_item: true,
            look_group: lookGroupId,
          }, { headers });
        }
      }
      
      // Mark look as rented (keep state for back navigation, clear on checkout completion)
      sessionStorage.setItem("mahra_build_look_rented", "true");
      
      const msg = lookMode === "edit_current" ? "Look updated in cart!" : "Look added to cart!";
      toast.success(msg);
      setLookMode(null);
      return true;
    } catch (err) {
      console.error("Failed to add look to cart:", err);
      toast.error("Failed to add look to cart");
      return false;
    }
  }, [slots, eventDate, rentalPeriod, deliveryMethod, itemCount, lookMode, cartLookItemIds]);

  // Check if attempting to change dates would reset look
  const wouldResetLook = useCallback((newDate, newPeriod) => {
    if (!eventDate && !rentalPeriod) return false;
    if (itemCount <= 1) return false;
    return (newDate && newDate !== eventDate) || (newPeriod && newPeriod !== rentalPeriod);
  }, [eventDate, rentalPeriod, itemCount]);

  // Get the next empty slot to highlight
  const getNextEmptySlot = useCallback(() => {
    if (!slots.outfit) return "outfit";
    if (!slots.jewellery) return "jewellery";
    if (!slots.clutch) return "clutch";
    if (!slots.accessory) return "accessory";
    return null;
  }, [slots]);

  const value = {
    // State
    isActive,
    slots,
    eventDate,
    rentalPeriod,
    activeSlot,
    aiRecommendations,
    loadingAI,
    
    // Computed
    items,
    itemCount,
    discountPercent,
    originalTotal,
    discountAmount,
    finalPrice,
    totalBond,
    progress,
    progressMessage: getProgressMessage(),
    nextEmptySlot: getNextEmptySlot(),
    
    // Actions
    startBuildLook,
    selectSlot,
    addToLook,
    lockEventDate,
    lockRentalPeriod,
    lockDeliveryMethod,
    deliveryMethod,
    removeFromSlot,
    replaceSlot,
    resetLook,
    exitBuildMode,
    fetchAIRecommendations,
    rentEntireLook,
    wouldResetLook,
    setActiveSlot,
    setIsActive,
    loadLookFromData,
    lookMode,
    setLookMode,
    cartLookItemIds,
    setCartLookItemIds,
  };

  return (
    <BuildLookContext.Provider value={value}>
      {children}
    </BuildLookContext.Provider>
  );
}

export function useBuildLook() {
  const ctx = useContext(BuildLookContext);
  if (!ctx) {
    throw new Error("useBuildLook must be used within BuildLookProvider");
  }
  return ctx;
}

export default BuildLookContext;
